import { TextField } from '@fluentui/react';
import { ConfirmDialog } from '@h2oai/ui-kit';
import { useEffect, useState } from 'react';

import { validateS3ObjectKey } from '../../../drive/s3Validation';
import { extractDisplayNames } from '../helpers';

export interface NameInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (name: string) => void;
  title?: string;
  confirmButtonText?: string;
  initialValue?: string;
  existingNames?: string[];
  generateUniqueName?: (existingNames: string[]) => string;
  currentItemName?: string;
}

export const NameInputModal = (props: NameInputModalProps) => {
  const {
    isOpen,
    onClose,
    onConfirm,
    title = 'Enter name',
    confirmButtonText = 'Confirm',
    initialValue = '',
    existingNames = [],
    generateUniqueName,
    currentItemName,
  } = props;

  const [name, setName] = useState(initialValue);

  useEffect(() => {
    if (isOpen) {
      if (generateUniqueName && !initialValue) {
        const uniqueName = generateUniqueName(existingNames);
        setName(uniqueName);
      } else {
        setName(initialValue);
      }
    }
  }, [isOpen, initialValue, existingNames, generateUniqueName]);

  const handleConfirm = () => {
    const trimmedName = name.trim();
    if (trimmedName) {
      onConfirm(trimmedName);
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  };

  const nameKey = name.trim();
  const hasNameChanged = nameKey !== initialValue;

  const existingDisplayNames = extractDisplayNames(existingNames);
  const filteredExistingNames = currentItemName
    ? existingDisplayNames.filter((name) => name !== currentItemName)
    : existingDisplayNames;

  const isDuplicate = filteredExistingNames.includes(nameKey);
  const basicValidation = validateS3ObjectKey(name);

  const validationResult = {
    ...basicValidation,
    isValid: basicValidation.isValid && !isDuplicate,
    errors: isDuplicate ? [...basicValidation.errors, 'An item with this name already exists'] : basicValidation.errors,
  };

  const isConfirmDisabled = !validationResult.isValid || !nameKey || !hasNameChanged;

  const shouldShowError = isOpen && hasNameChanged;
  const displayValidation = {
    ...validationResult,
    errors: shouldShowError ? validationResult.errors : [],
    isValid: shouldShowError ? validationResult.isValid : true,
  };

  const content = (
    <TextField
      value={name}
      onChange={(_, newValue) => setName(newValue || '')}
      errorMessage={displayValidation.isValid ? undefined : displayValidation.errors[0]}
      autoFocus
      onRenderLabel={() => null}
      styles={{
        root: { marginBottom: 8 },
        field: { fontSize: 14 },
      }}
    />
  );

  return (
    <ConfirmDialog
      modalProps={{ isBlocking: false }}
      hidden={!isOpen}
      content={content}
      title={title}
      onConfirm={handleConfirm}
      onDismiss={handleClose}
      confirmationButtonText={confirmButtonText}
      confirmationButtonDisabled={isConfirmDisabled}
    />
  );
};
